import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useState } from 'react';
import { adminApi } from '../../services/adminApi';
import QRCode from 'qrcode';

export const MobileEnrollmentQrPanel: React.FC = () => {
  useDateTimePreferences();
  const [apiBaseUrl, setApiBaseUrl] = useState<string>((import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''));
  const [tenantSlug, setTenantSlug] = useState<string>('demo');
  const [tenantName, setTenantName] = useState<string>('Demo Tenant');
  const [env, setEnv] = useState<string>('prod-au');
  const [expiresInSeconds, setExpiresInSeconds] = useState<string>('600');
  const [payloadJson, setPayloadJson] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  const handleGenerate = async () => {
    setBusy(true);
    setStatus('');
    setExpiresAt(null);
    try {
      const response = await adminApi.generateMobileEnrollmentQr({
        apiBaseUrl: apiBaseUrl.trim(),
        tenantSlug: tenantSlug.trim() || undefined,
        tenantName: tenantName.trim() || undefined,
        env: env.trim() || undefined,
        expiresInSeconds: Number(expiresInSeconds) || 600,
      });
      setPayloadJson(response.payloadJson || '');
      const nextQr = await QRCode.toDataURL(response.payloadJson || '', {
        errorCorrectionLevel: 'L',
        margin: 2,
        width: 420,
      });
      setQrDataUrl(nextQr);
      setExpiresAt(response.expiresAtUnix * 1000);
      setStatus('Generated enrollment payload');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Failed to generate enrollment payload');
    } finally {
      setBusy(false);
    }
  };

  const handleCopyPayload = async () => {
    if (!payloadJson) {
      setStatus('No payload available to copy');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payloadJson);
        setStatus('Payload JSON copied to clipboard');
        return;
      }
      setStatus('Clipboard API not available in this browser');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Failed to copy payload');
    }
  };

  return (
    <div className="management-section mobile-enrollment-panel">
      <div className="section-header">
        <div>
          <h2>Mobile Enrollment</h2>
          <p className="panel-subtitle">Generate a tenant-scoped onboarding payload and QR code for first-run mobile setup.</p>
        </div>
      </div>

      <div className="mobile-enrollment-grid">
        <div className="card mobile-enrollment-config">
          <div className="form-row">
            <div className="form-group">
              <label>API base URL</label>
              <input value={apiBaseUrl} onChange={(e) => setApiBaseUrl(e.target.value)} placeholder="https://zurvit-func-01.azurewebsites.net" />
            </div>
            <div className="form-group">
              <label>Environment label</label>
              <input value={env} onChange={(e) => setEnv(e.target.value)} placeholder="prod-au" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tenant slug</label>
              <input value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} placeholder="demo" />
            </div>
            <div className="form-group">
              <label>Tenant name</label>
              <input value={tenantName} onChange={(e) => setTenantName(e.target.value)} placeholder="Demo Tenant" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Expiry seconds</label>
              <input value={expiresInSeconds} onChange={(e) => setExpiresInSeconds(e.target.value)} placeholder="600" />
            </div>
          </div>

          <div className="mobile-enrollment-actions">
            <button className="btn-primary" disabled={busy} onClick={handleGenerate}>{busy ? 'Generating...' : 'Generate payload + QR'}</button>
            <button className="btn-secondary" disabled={!payloadJson} onClick={() => void handleCopyPayload()}>Copy payload JSON</button>
          </div>

          {status ? <p className="mobile-enrollment-status">{status}</p> : null}
          {expiresAt ? <p className="mobile-enrollment-status">Expires: {formatDateTime(expiresAt)}</p> : null}

          <div className="mobile-enrollment-howto">
            <h4>How to use</h4>
            <ol>
              <li>Select the tenant and backend environment above.</li>
              <li>Tap <strong>Generate payload + QR</strong>.</li>
              <li>On a fresh mobile install, open first-run onboarding and choose <strong>Scan QR</strong>.</li>
              <li>If camera is unavailable, paste the payload JSON into the app onboarding text box.</li>
              <li>Continue onboarding, then send magic link login.</li>
            </ol>
          </div>
        </div>

        <div className="card mobile-enrollment-output">
          <h4>Enrollment QR</h4>
          {qrDataUrl ? (
            <div className="mobile-enrollment-qr-wrap">
              <img src={qrDataUrl} alt="Mobile enrollment QR" className="mobile-enrollment-qr" />
            </div>
          ) : (
            <p className="table-info">Generate a payload to render QR.</p>
          )}

          <div className="form-group">
            <label>Payload JSON</label>
            <textarea value={payloadJson} onChange={() => undefined} rows={10} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileEnrollmentQrPanel;
